import type { SpotKey } from '../../core/spots';
import type { CombineLevel } from '../../schemas/drill';
import { levelInfo, levelSpots } from './catalog';
import { type AttemptLike, groupTotals, type PlayerTally, rate, tallyAttempts } from './tally';

/**
 * What a finished level says: a ranking, percentiles against the group, hot and cold spots,
 * the best streak and the brick count. All numbers come from the shots. The rating itself is the
 * stats engine's job (R17), the combine only records.
 */

export type Zone = 'hot' | 'cold' | 'neutral';

/** A percentile against one or two other shooters says nothing, so it needs at least this many who shot. */
export const PERCENTILE_MIN_SHOOTERS = 3;

/** A spot is hot or cold when it sits this many points of percentage from the player's own mean. */
export const ZONE_GAP = 0.12;

export interface LevelRow {
  playerId: string;
  makes: number;
  att: number;
  /** 0..1, or null with no shots. */
  pct: number | null;
  rank: number;
  /**
   * Share of the other shooters in this run this player beat, 0..100. null when this player did not
   * shoot or fewer than PERCENTILE_MIN_SHOOTERS people did.
   */
  percentile: number | null;
  bestSpot: SpotKey | null;
  worstSpot: SpotKey | null;
  zones: Partial<Record<SpotKey, Zone>>;
  bricks: number;
  bestStreak: number;
  /** Took every shot the level asks for. */
  complete: boolean;
}

export interface LevelResults {
  level: CombineLevel;
  rows: LevelRow[];
  group: { makes: number; att: number; pct: number | null };
  /** The leader, or null when nobody shot. */
  topPlayerId: string | null;
}

function zonesOf(t: PlayerTally): Partial<Record<SpotKey, Zone>> {
  const mean = rate(t.makes, t.att);
  const out: Partial<Record<SpotKey, Zone>> = {};
  for (const [spot, s] of Object.entries(t.bySpot) as [SpotKey, { makes: number; att: number }][]) {
    const p = rate(s.makes, s.att);
    if (p === null || mean === null) continue;
    out[spot] = p >= mean + ZONE_GAP ? 'hot' : p <= mean - ZONE_GAP ? 'cold' : 'neutral';
  }
  return out;
}

function extremeSpot(t: PlayerTally, best: boolean): SpotKey | null {
  let pick: SpotKey | null = null;
  let pickRate = best ? -1 : 2;
  for (const spot of Object.keys(t.bySpot) as SpotKey[]) {
    const s = t.bySpot[spot];
    const p = s ? rate(s.makes, s.att) : null;
    if (p === null) continue;
    if (best ? p > pickRate : p < pickRate) {
      pick = spot;
      pickRate = p;
    }
  }
  return pick;
}

export function levelResults(
  level: CombineLevel,
  playerIds: readonly string[],
  attempts: readonly AttemptLike[],
): LevelResults {
  const tallies = tallyAttempts(attempts, playerIds);
  const need = levelInfo(level).shots ?? 0;
  const list = playerIds.map((id) => tallies.get(id) as PlayerTally);
  const sorted = [...list].sort(
    (a, b) => (rate(b.makes, b.att) ?? -1) - (rate(a.makes, a.att) ?? -1) || b.makes - a.makes,
  );
  const pcts = list.map((t) => rate(t.makes, t.att));
  const shooters = pcts.filter((q): q is number => q !== null);
  const rows = sorted.map((t, i): LevelRow => {
    const p = rate(t.makes, t.att);
    let percentile: number | null = null;
    if (shooters.length >= PERCENTILE_MIN_SHOOTERS && p !== null) {
      let below = 0;
      let same = 0;
      for (const q of shooters) {
        if (q < p) below += 1;
        else if (q === p) same += 1;
      }
      percentile = Math.round(((below + (same - 1) / 2) / (shooters.length - 1)) * 100);
    }
    const prev = sorted[i - 1];
    const tied = prev && rate(prev.makes, prev.att) === p && prev.makes === t.makes;
    return {
      playerId: t.playerId,
      makes: t.makes,
      att: t.att,
      pct: p,
      rank: tied ? 0 : i + 1,
      percentile,
      bestSpot: extremeSpot(t, true),
      worstSpot: extremeSpot(t, false),
      zones: zonesOf(t),
      bricks: t.att - t.makes,
      bestStreak: t.bestStreak,
      complete: need > 0 && t.att >= need,
    };
  });
  // Ties share the rank of the first of the tie.
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i] as LevelRow;
    if (r.rank === 0) r.rank = (rows[i - 1] as LevelRow).rank;
  }
  const g = groupTotals(tallies.values());
  const top = rows[0];
  return {
    level,
    rows,
    group: { makes: g.makes, att: g.att, pct: rate(g.makes, g.att) },
    topPlayerId: top && top.att > 0 ? top.playerId : null,
  };
}

export interface SpotRow {
  playerId: string;
  makes: number;
  att: number;
  /** Leads the spot (ties share it) with at least one make. */
  hot: boolean;
}

/** The mini leaderboard of one spot, best first. The top shooter (and ties) get the flame. */
export function spotBoard(spot: SpotKey, playerIds: readonly string[], attempts: readonly AttemptLike[]): SpotRow[] {
  const tallies = tallyAttempts(
    attempts.filter((a) => a.spot === spot),
    playerIds,
  );
  const rows = playerIds.map((id) => {
    const t = tallies.get(id) as PlayerTally;
    return { playerId: id, makes: t.makes, att: t.att };
  });
  rows.sort((a, b) => b.makes - a.makes || a.att - b.att);
  const best = rows[0]?.makes ?? 0;
  return rows.map((r) => ({ ...r, hot: best > 0 && r.makes === best }));
}

/** Spots of a level with each player's made/att, for the heat diagram of one player. */
export function spotHeat(
  level: CombineLevel,
  playerId: string,
  attempts: readonly AttemptLike[],
): Partial<Record<SpotKey, { made: number; att: number }>> {
  const t = tallyAttempts(attempts, [playerId]).get(playerId) as PlayerTally;
  const out: Partial<Record<SpotKey, { made: number; att: number }>> = {};
  for (const spot of levelSpots(level)) {
    const s = t.bySpot[spot];
    if (s) out[spot] = { made: s.makes, att: s.att };
  }
  return out;
}
